import type { GameState, Seat, Team } from "./types";

export type SwingKind =
  | "own-euchred"
  | "opp-euchred"
  | "own-march"
  | "opp-march"
  | "own-lone-march"
  | "opp-lone-march"
  | "own-lone-fail"
  | "opp-lone-fail";

export interface SwingMoment {
  /** 1-based hand number within the match. */
  handNumber: number;
  kind: SwingKind;
  maker: Seat;
  makerTeam: Team;
  /** Points the hand awarded; direction is encoded in `kind`. */
  points: number;
}

/**
 * Extracts the biggest score swings of a completed match from the history
 * (same parsing pattern as matchMetrics): euchred calls, marches and lone
 * results. Plain one-point makes are not swings and are left out. Ordered by
 * points descending, then by hand order.
 */
export function swingMoments(state: GameState): SwingMoment[] {
  const moments: SwingMoment[] = [];
  let maker: Seat | null = null;
  let alone = false;
  let handNumber = 0;
  for (const event of state.history) {
    const call = event.match(/^([0-3]):(?:order|call):[a-z]+(:alone)?$/);
    if (call) {
      maker = Number(call[1]) as Seat;
      alone = Boolean(call[2]);
      continue;
    }
    const hand = event.match(/^hand:(made|march|euchred|lone-made|lone-march):(-?\d+)-(-?\d+)$/);
    if (!hand || maker === null) continue;
    handNumber += 1;
    const makerTeam = (maker % 2) as Team;
    const own = makerTeam === 0;
    const points = Math.max(Number(hand[2]), Number(hand[3]));
    let kind: SwingKind | null = null;
    if (hand[1] === "euchred" && alone) kind = own ? "own-lone-fail" : "opp-lone-fail";
    else if (hand[1] === "euchred") kind = own ? "own-euchred" : "opp-euchred";
    else if (hand[1] === "lone-march") kind = own ? "own-lone-march" : "opp-lone-march";
    else if (hand[1] === "march") kind = own ? "own-march" : "opp-march";
    if (kind) moments.push({ handNumber, kind, maker, makerTeam, points });
    maker = null;
    alone = false;
  }
  return moments.sort((a, b) => b.points - a.points || a.handNumber - b.handNumber);
}
